import { createGlobalState } from "@vueuse/core";
import { computed, readonly, ref } from "vue";
import { getWalletBalance } from "../api";
import type { IWalletBalance } from "../api";

const useWalletPaymentGlobal = createGlobalState(() => {
  const walletData = ref<IWalletBalance | null>(null);
  const loading = ref(false);

  /** Whether the user has toggled on the "use wallet balance" option. */
  const useWallet = ref(false);

  const walletBalance = computed(() => walletData.value?.balance ?? 0);
  const isWalletActive = computed(() => walletData.value?.isActive ?? false);
  const walletCurrency = computed(() => walletData.value?.currency ?? "");

  const CURRENCY_LABELS: Record<string, string> = {
    IRR: "ریال",
    IRT: "تومان",
  };
  const walletCurrencyLabel = computed(() => CURRENCY_LABELS[walletCurrency.value] ?? walletCurrency.value);

  /**
   * Fetches (or re-fetches) the wallet balance for the given customerId.
   * Call this on checkout initialization and again just before order submission.
   */
  async function fetchWalletBalance(customerId: string): Promise<void> {
    if (!customerId) {
      walletData.value = null;
      return;
    }
    loading.value = true;
    try {
      walletData.value = await getWalletBalance(customerId);
    } catch {
      walletData.value = null;
    } finally {
      loading.value = false;
    }
  }

  /**
   * How much of the order total is covered by the wallet.
   * = min(walletBalance, orderTotal)
   */
  function walletDeductionAmount(orderTotal: number): number {
    return Math.min(walletBalance.value, orderTotal);
  }

  /**
   * True when wallet balance alone covers the entire order total.
   */
  function isWalletOnly(orderTotal: number): boolean {
    return walletBalance.value >= orderTotal;
  }

  /** Reset toggle (call on checkout clear / navigation away) */
  function resetWalletToggle(): void {
    useWallet.value = false;
  }

  return {
    walletData: readonly(walletData),
    walletBalance,
    isWalletActive,
    walletCurrency,
    walletCurrencyLabel,
    useWallet,
    loading: readonly(loading),
    fetchWalletBalance,
    walletDeductionAmount,
    isWalletOnly,
    resetWalletToggle,
  };
});

export function useWalletPayment() {
  return useWalletPaymentGlobal();
}
